import { z } from "zod"

import {
  CLASS_TYPE_VALUE,
  CommunicationStudioClasses,
  CommunicationStudioClassValue,
} from "@tfg/types/constants/communication-studio.constant"
import { ZodFormObjectTypeDescription } from "@tfg/types/constants/zodFromInputTypes"
import {
  CSSColorSchema,
  cssTextDecorationSchema,
  cssLengthSchema,
  fontWeightSchema,
  TextAlignEnum,
  TextTransformSchema,
  FontFamilySchema,
  cssLineHeightSchema,
  alignSchema,
} from "@tfg/types/schemas/communication-studio/cssStylesSchema"
import { getSchemaKeysAsNull } from "@tfg/types/schemas/utils"

export const BaseClassSchema = z.object({
  name: z.string(),
  type: z.literal(CLASS_TYPE_VALUE).readonly(),
  props: z.record(z.string(), z.string().nullable()),
})
export type BaseClass = z.infer<typeof BaseClassSchema>
const makePropsNullable = <P extends z.ZodRawShape>(
  props: z.ZodObject<P>
): z.ZodObject<{ [K in keyof P]: z.ZodNullable<P[K]> }> => {
  const nullableShape = Object.entries(props.shape).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: (value as z.ZodTypeAny).nullable(),
    }),
    {} as { [K in keyof P]: z.ZodNullable<P[K]> }
  )

  return z.object(nullableShape) as z.ZodObject<{
    [K in keyof P]: z.ZodNullable<P[K]>
  }>
}

export const createBaseClassInstance = <
  N extends string,
  P extends z.ZodRawShape,
>(
  name: N,
  props: z.ZodObject<P>
) =>
  BaseClassSchema.extend({
    name: z.literal(name),
    props: makePropsNullable(props),
  })

export const LogoClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.LOGO,
  z.object({
    height: cssLengthSchema,
    width: cssLengthSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type LogoClass = z.infer<typeof LogoClassSchema>

export const OrganizationTitleClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.ORGANIZATION_TITLE,
  z.object({
    fontSize: cssLengthSchema,
    fontWeight: fontWeightSchema,
    textTransform: TextTransformSchema,
    textAlign: TextAlignEnum,
    backgroundColor: CSSColorSchema,
    fontFamily: FontFamilySchema,
    color: CSSColorSchema,
    lineHeight: cssLineHeightSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type OrganizationTitleClass = z.infer<
  typeof OrganizationTitleClassSchema
>

export const ProductImageClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.PRODUCT_IMAGE,
  z.object({
    width: cssLengthSchema,
    height: cssLengthSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type ProductImageClass = z.infer<typeof ProductImageClassSchema>

export const ActivityClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.ACTIVITY,
  z.object({
    fontSize: cssLengthSchema,
    fontWeight: fontWeightSchema,
    textTransform: TextTransformSchema,
    letterSpacing: cssLengthSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type ActivityClass = z.infer<typeof ActivityClassSchema>

export const CountryClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.COUNTRY,
  z.object({
    fontSize: cssLengthSchema,
    fontWeight: fontWeightSchema,
    textTransform: TextTransformSchema,
    letterSpacing: cssLengthSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type CountryClass = z.infer<typeof CountryClassSchema>

export const PackagingInfoHyperLinkClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.PACKAGING_INFO_HYPER_LINK,
  z.object({
    fontWeight: fontWeightSchema,
    color: CSSColorSchema,
    textDecoration: cssTextDecorationSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type PackagingInfoHyperLinkClass = z.infer<
  typeof PackagingInfoHyperLinkClassSchema
>

export const PackagingInfoHeaderClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.PACKAGING_INFO_HEADER,
  z.object({
    fontWeight: fontWeightSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type PackagingInfoHeaderClass = z.infer<
  typeof PackagingInfoHeaderClassSchema
>

export const SearchEngineHeaderClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.SEARCH_ENGINE_HEADER,
  z.object({
    color: CSSColorSchema,
    backgroundColor: CSSColorSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type SearchEngineHeaderClass = z.infer<
  typeof SearchEngineHeaderClassSchema
>

export const MainContainerClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.MAIN_CONTAINER,
  z.object({
    textAlign: TextAlignEnum,
    textTransform: TextTransformSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type MainContainerClass = z.infer<typeof MainContainerClassSchema>

export const AppHeaderClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.APP_HEADER,
  z.object({
    backgroundColor: CSSColorSchema,
    marginBottom: cssLengthSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type AppHeaderClass = z.infer<typeof AppHeaderClassSchema>

export const ContentBlocksClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.CONTENT_BLOCKS,
  z.object({
    gap: cssLengthSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const LastUpdateClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.LAST_UPDATE,
  z.object({
    fontSize: cssLengthSchema,
    fontWeight: fontWeightSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProductNameClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.PRODUCT_NAME,
  z.object({
    fontSize: cssLengthSchema,
    fontWeight: fontWeightSchema,
    textTransform: TextTransformSchema,
    color: CSSColorSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProductReferenceClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.PRODUCT_REFERENCE,
  z.object({
    fontSize: cssLengthSchema,
    fontWeight: fontWeightSchema,
    textTransform: TextTransformSchema,
    color: CSSColorSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProductTitleClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.PRODUCT_TITLE,
  z.object({
    gap: cssLengthSchema,
    marginBottom: cssLengthSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProductAttributesContainerClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.PRODUCT_ATTRIBUTES_CONTAINER,
  z.object({
    gap: cssLengthSchema,
    marginBottom: cssLengthSchema,
    alignItems: alignSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type ProductAttributesContainerClass = z.infer<
  typeof ProductAttributesContainerClassSchema
>
export const RecycledPackagingKeyInfoClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO,
  z.object({
    fontWeight: fontWeightSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const EcoscoreHeaderClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.ECOSCORE_HEADER,
  z.object({
    fill: CSSColorSchema,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProdcutAttributesPrimaryInformationClassSchema =
  createBaseClassInstance(
    CommunicationStudioClasses.PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION,
    z.object({
      fontSize: cssLengthSchema,
      fontWeight: fontWeightSchema,
      textTransform: TextTransformSchema,
      color: CSSColorSchema,
      textAlign: TextAlignEnum,
      fontFamily: FontFamilySchema,
    })
  ).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProdcutAttributesSecondaryInformationClassSchema =
  createBaseClassInstance(
    CommunicationStudioClasses.PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION,
    z.object({
      fontSize: cssLengthSchema,
      fontWeight: fontWeightSchema,
      textTransform: TextTransformSchema,
      color: CSSColorSchema,
      textAlign: TextAlignEnum,
      fontFamily: FontFamilySchema,
    })
  ).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const GenericPassportTitleClassSchema = createBaseClassInstance(
  CommunicationStudioClasses.GENERIC_PASSPORT_TITLE,
  z.object({
    color: CSSColorSchema,
    backgroundColor: CSSColorSchema,
    fontSize: cssLengthSchema,
    textTransform: TextTransformSchema,
    fontWeight: fontWeightSchema,
    textDecoration: cssTextDecorationSchema,
    lineHeight: cssLengthSchema,
    letterSpacing: cssLengthSchema,
    fontFamily: z.string().nullable(),
    textAlign: TextAlignEnum,
  })
).describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)
export type GenericPassportTitleClass = z.infer<
  typeof GenericPassportTitleClassSchema
>

export const CLASS_SCHEMA_MAP = {
  [CommunicationStudioClasses.LOGO]: LogoClassSchema,
  [CommunicationStudioClasses.ACTIVITY]: ActivityClassSchema,
  [CommunicationStudioClasses.COUNTRY]: CountryClassSchema,
  [CommunicationStudioClasses.PACKAGING_INFO_HYPER_LINK]:
    PackagingInfoHyperLinkClassSchema,
  [CommunicationStudioClasses.PACKAGING_INFO_HEADER]:
    PackagingInfoHeaderClassSchema,
  [CommunicationStudioClasses.SEARCH_ENGINE_HEADER]:
    SearchEngineHeaderClassSchema,
  [CommunicationStudioClasses.MAIN_CONTAINER]: MainContainerClassSchema,
  [CommunicationStudioClasses.APP_HEADER]: AppHeaderClassSchema,
  [CommunicationStudioClasses.CONTENT_BLOCKS]: ContentBlocksClassSchema,
  [CommunicationStudioClasses.LAST_UPDATE]: LastUpdateClassSchema,
  [CommunicationStudioClasses.PRODUCT_NAME]: ProductNameClassSchema,
  [CommunicationStudioClasses.PRODUCT_REFERENCE]: ProductReferenceClassSchema,
  [CommunicationStudioClasses.PRODUCT_TITLE]: ProductTitleClassSchema,
  [CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO]:
    RecycledPackagingKeyInfoClassSchema,
  [CommunicationStudioClasses.ECOSCORE_HEADER]: EcoscoreHeaderClassSchema,
  [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION]:
    ProdcutAttributesPrimaryInformationClassSchema,
  [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION]:
    ProdcutAttributesSecondaryInformationClassSchema,
  [CommunicationStudioClasses.ORGANIZATION_TITLE]: OrganizationTitleClassSchema,
  [CommunicationStudioClasses.PRODUCT_IMAGE]: ProductImageClassSchema,
  [CommunicationStudioClasses.GENERIC_PASSPORT_TITLE]:
    GenericPassportTitleClassSchema,
  [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_CONTAINER]:
    ProductAttributesContainerClassSchema,
} as const satisfies Record<
  (typeof CommunicationStudioClasses)[keyof typeof CommunicationStudioClasses],
  ReturnType<typeof createBaseClassInstance>
>
export type CommunicationStudioClass = z.infer<
  (typeof CLASS_SCHEMA_MAP)[keyof typeof CLASS_SCHEMA_MAP]
>

export const CLASS_SKELETONS = Object.entries(CLASS_SCHEMA_MAP).reduce<
  Record<CommunicationStudioClassValue, Record<string, null>>
>(
  (acc, [key, schema]) => {
    acc[key as CommunicationStudioClassValue] = getPropsSchemaKeysAsNull(schema)
    return acc
  },
  {} as Record<CommunicationStudioClassValue, Record<string, null>>
)

export function getPropsSchemaKeysAsNull(
  classSchema: ReturnType<typeof createBaseClassInstance> | z.ZodTypeAny
): Record<string, null> {
  let actualSchema = classSchema

  if (actualSchema instanceof z.ZodObject) {
    const shape = actualSchema.shape

    if (shape.props) {
      let propsSchema = shape.props

      while (
        propsSchema instanceof z.ZodOptional ||
        propsSchema instanceof z.ZodNullable
      ) {
        propsSchema = propsSchema.def.innerType
      }

      return getSchemaKeysAsNull(propsSchema)
    }
  }

  return {}
}
